import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { TwoColumnLayout } from '@factorialco/f0-react'
import { QuotePricingPageDocument, QuotesTabsVisibilityDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import MoneyWithCurrency from 'modules/core/lib/money'

import QuotePhasesTable from 'modules/finance/components/Quotes/QuotePhasesTable'
import { ConvertToProjectModal } from 'modules/finance/components/Quotes/modals/ConvertToProjectModal'
import { useConvertQuoteIntoInvoice } from 'modules/finance/hooks/quotes/useConvertQuoteIntoInvoice'
import { useDownloadQuotePdf } from 'modules/finance/hooks/quotes/useDownloadQuotePdf'
import { onHeaderActionClick } from 'modules/finance/lib/quotesUtils'
import QuotePrincingFinancialSumary from 'modules/finance/pages/QuotePricingPage/_QuotePrincingFinancialSumary'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

const QuotePricingPage = () => {
  const { quoteId } = useParams()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [showConvertToProjectModal, setShowConvertToProjectModal] = useState(false)
  const {
    access: { locale },
    company: { legalEntities, currency: companyCurrency },
  } = useCurrentSession()
  const downloadQuotePdf = useDownloadQuotePdf()
  const convertQuoteIntoInvoice = useConvertQuoteIntoInvoice({
    refetchQueries: [QuotesTabsVisibilityDocument],
  })

  const navigateToProject = () => {
    quote?.projectId &&
      navigate(
        navigation.projects.id.details.overview.fullPath({
          id: String(quote.projectId),
        })
      )
  }
  const navigateToInvoice = () => {
    quoteFinancialDocumentId &&
      navigate(
        navigation.finance.sales.salesInvoices.detail.fullPath({
          invoiceId: Number(quoteFinancialDocumentId),
        })
      )
  }
  const { data, error, loading } = useQuery(QuotePricingPageDocument, {
    variables: {
      id: Number(quoteId),
    },
  })
  const authorized = !!data?.finance?.quote?._policies?.manage?.can
  const legalEntityMainCurrency =
    legalEntities?.find((legalEntity) => legalEntity.main)?.currency || companyCurrency || 'EUR'

  const quote = data?.finance?.quote
  const quoteFinancialDocumentId = quote?.financialDocumentAssociation?.financialDocument?.id
  const lineItems = quote?.financeLineItems

  const totalCost = MoneyWithCurrency({
    optionalDecimals: true,
    amount: Number(quote?.totalAmountCents) || 0,
    currency: quote?.currency ?? '',
    locale,
  }).toString()

  return (
    <ResponsivePage.Body
      variant="fullscreen"
      authorized={authorized}
      loading={loading}
      error={error}
      onActionClick={(key) =>
        onHeaderActionClick(
          key,
          Number(quoteId),
          () => setShowConvertToProjectModal(true),
          convertQuoteIntoInvoice,
          downloadQuotePdf,
          navigateToProject,
          navigateToInvoice
        )
      }
    >
      {/* TODO: change Layout */}
      <TwoColumnLayout
        sideContent={
          <QuotePrincingFinancialSumary
            totalHours="-"
            estimatedCosts="-"
            subtotal="-"
            total={totalCost}
          />
        }
      >
        <QuotePhasesTable
          currency={quote?.currency ?? legalEntityMainCurrency}
          lineItems={lineItems || []}
          isProductQuote={quote?.quoteType == 'products'}
        />
      </TwoColumnLayout>
      {showConvertToProjectModal && quote && (
        <ConvertToProjectModal
          isOpen={showConvertToProjectModal}
          onClose={() => setShowConvertToProjectModal(false)}
          currency={quote.currency ?? ''}
          quoteId={quote.id}
          defaultProjectName={quote.name ?? ''}
          refetchQueries={[QuotesTabsVisibilityDocument]}
          totalBudget={+quote.totalAmountCents}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default QuotePricingPage
